import {
  revitBridge,
  type ExpectedRevitDocumentIdentity,
} from '../../api/revit/revitBridge';
import type { RevitOperationProgressSnapshot } from '../../api/grpc/operationProgressClient';
import { webLog } from '../../api/grpc/webLogger';
import { sanitizeRevitParameterNames } from '../../lib/revitParameterNames';
import { applyParameterTypeHints } from '../../lib/revit/wizardParameterDataTypes';
import { useSharedParameterStore } from '../../stores/useSharedParameterStore';
import type { ConnectorParameterBindingKind } from '../../types';
import type { BoltParameterTypeHint } from './boltParameterTypes';

export interface BoltConfigurationOptions {
  expectedDocumentIdentity?: ExpectedRevitDocumentIdentity;
  onProgress?: (progress: RevitOperationProgressSnapshot) => void;
}

interface ProjectBoltParameterResult {
  success: boolean;
  addedCount: number;
  updatedCount?: number;
  existingCount?: number;
  failedCount?: number;
  conflictingCount?: number;
  error?: string;
}

interface ProjectBatchCounts {
  addedCount: number;
  updatedCount: number;
  existingCount: number;
  failedCount: number;
  conflictingCount: number;
}

function summarizeProjectBatchMessage(message: string | undefined): ProjectBatchCounts {
  const match = message?.match(
    /Created\s+(\d+)\s+project parameters,\s+updated\s+(\d+),\s+(\d+)\s+already existed,\s+(\d+)\s+conflicts,\s+(\d+)\s+failed/i,
  );
  if (!match) {
    return {
      addedCount: 0,
      updatedCount: 0,
      existingCount: 0,
      conflictingCount: 0,
      failedCount: 0,
    };
  }

  return {
    addedCount: Number(match[1] ?? 0),
    updatedCount: Number(match[2] ?? 0),
    existingCount: Number(match[3] ?? 0),
    conflictingCount: Number(match[4] ?? 0),
    failedCount: Number(match[5] ?? 0),
  };
}

function resolveSharedParameterDefinitions(parameterNames: string[]): {
  definitions: Array<{
    name: string;
    guid: string;
    dataType: string;
    groupId: number;
    description?: string;
  }>;
  missingNames: string[];
} {
  const parsedParameters = useSharedParameterStore.getState().parsedParameters as Array<{
    name: string;
    guid: string;
    dataType: string;
    groupId: number;
    description?: string;
  }>;
  const parameterLookup = new Map(
    parsedParameters.map((parameter) => [parameter.name.trim().toLowerCase(), parameter]),
  );
  const definitions: Array<{
    name: string;
    guid: string;
    dataType: string;
    groupId: number;
    description?: string;
  }> = [];
  const missingNames: string[] = [];

  sanitizeRevitParameterNames(parameterNames).forEach((parameterName) => {
    const matchingDefinition = parameterLookup.get(parameterName.trim().toLowerCase());
    if (matchingDefinition) {
      definitions.push(matchingDefinition);
    } else {
      missingNames.push(parameterName);
    }
  });

  return { definitions, missingNames };
}

export async function addProjectBoltParameters(
  parameterNames: string[],
  categories: string[],
  bindingKind: ConnectorParameterBindingKind,
  parameterTypeHints: BoltParameterTypeHint[],
  options: BoltConfigurationOptions,
): Promise<ProjectBoltParameterResult> {
  webLog.info('parameterSetup', `bolt project parameter dispatch ${JSON.stringify({
    requestedBinding: bindingKind,
    parameterCount: parameterNames.length,
    parameterNames,
    categoryCount: categories.length,
    categories,
  })}`);

  if (bindingKind === 'instance') {
    const { definitions, missingNames } = resolveSharedParameterDefinitions(parameterNames);
    const typeHintLookup = new Map(
      parameterTypeHints.map((hint) => [hint.name.trim().toLowerCase(), hint.dataType]),
    );
    const resolvedDefinitions = applyParameterTypeHints(definitions, parameterTypeHints);
    const unresolvedNames: string[] = [];
    for (const missingName of missingNames) {
      const hintedType = typeHintLookup.get(missingName.trim().toLowerCase());
      if (!hintedType) {
        unresolvedNames.push(missingName);
        continue;
      }
      resolvedDefinitions.push({
        name: missingName,
        guid: '',
        dataType: hintedType,
        groupId: 0,
      });
    }
    if (unresolvedNames.length > 0) {
      return {
        success: false,
        addedCount: 0,
        error: `Shared parameter definitions missing for: ${unresolvedNames.join(', ')}`,
      };
    }

    const result = await revitBridge.createParametersFromParsedFile({
      parameters: resolvedDefinitions,
      categories,
      isType: false,
      ...options.expectedDocumentIdentity,
      onProgress: options.onProgress,
    });
    if (!result.success || !result.data) {
      return {
        success: false,
        addedCount: 0,
        failedCount: result.data?.failureCount ?? 0,
        error: result.error || 'Failed to add instance project parameters',
      };
    }

    const failedCount = result.data.results.filter((entry) => !entry.success).length;
    return {
      success: failedCount === 0,
      addedCount: result.data.successCount,
      failedCount,
      error: failedCount > 0
        ? result.error || 'Some project parameters failed during setup'
        : undefined,
    };
  }

  const result = await revitBridge.addProjectParameters(
    parameterNames,
    categories,
    options.expectedDocumentIdentity,
    {
      bindingKind,
      onProgress: options.onProgress,
      parameterTypeHints,
    },
  );
  if (!result.success) {
    return {
      success: false,
      addedCount: 0,
      error: result.error || 'Failed to add project parameters',
    };
  }

  const counts = summarizeProjectBatchMessage(result.data?.message);
  return {
    success: counts.failedCount === 0 && counts.conflictingCount === 0,
    ...counts,
    error: counts.failedCount > 0 || counts.conflictingCount > 0
      ? result.data?.message || result.error || 'Some project parameters failed during setup'
      : undefined,
  };
}
